import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

export function useMobileHorizontalScroll() {
  const { isDesktopDevice: isDesktop } = useContext(CurrentDeviceContext);
  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const [scrollElReady, setScrollElReady] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const horizontalScrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollElRef.current = node;
    setScrollElReady(!!node);
  }, []);

  const updateScrollThumb = useCallback(() => {
    const scrollContainer = scrollElRef.current;
    const scrollThumb = scrollThumbRef.current;
    if (!scrollContainer || !scrollThumb) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;

    const progress = scrollLeft / maxScroll;
    const trackWidth = scrollThumb.parentElement?.offsetWidth || 0;
    const thumbWidth = scrollThumb.offsetWidth;
    scrollThumb.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
  }, []);

  useEffect(() => {
    if (isDesktop || !scrollElReady) return;

    const scrollContainer = scrollElRef.current;
    if (!scrollContainer) return;

    const checkScrollable = () => {
      setIsScrollable(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    };

    updateScrollThumb();
    checkScrollable();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollThumb();
      checkScrollable();
    });
    resizeObserver.observe(scrollContainer);

    scrollContainer.addEventListener("scroll", updateScrollThumb);

    return () => {
      scrollContainer.removeEventListener("scroll", updateScrollThumb);
      resizeObserver.disconnect();
    };
  }, [isDesktop, scrollElReady, updateScrollThumb]);

  return { isDesktop, horizontalScrollRef, scrollThumbRef, updateScrollThumb, isScrollable };
}
